import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ShieldCheck, ShieldAlert, Shield, ArrowRight, FileText, 
  BookOpen, HelpCircle, Layers, ZoomIn 
} from 'lucide-react';

const ISOResources = () => {
  const navigate = useNavigate();

  const levels = [
    {
      id: 1,
      title: "Acceptance Level 1",
      description: "Highest stringency. Often used for aerospace or high-pressure vessels.",
      icon: <ShieldCheck className="text-cyan-600" size={32} />,
      path: "/resources/iso/level-1",
      color: "border-cyan-200 hover:border-cyan-500",
      bgColor: "bg-cyan-50"
    },
    {
      id: 2,
      title: "Acceptance Level 2",
      description: "Standard industrial level. Aligned with Quality Level B in ISO 5817.",
      icon: <Shield className="text-indigo-600" size={32} />,
      path: "/resources/iso/level-2",
      color: "border-indigo-200 hover:border-indigo-500",
      bgColor: "bg-indigo-50"
    },
    {
      id: 3,
      title: "Acceptance Level 3",
      description: "Lowest stringency. Suitable for general structural steel (Quality Level C).",
      icon: <ShieldAlert className="text-blue-600" size={32} />,
      path: "/resources/iso/level-3",
      color: "border-blue-200 hover:border-blue-500",
      bgColor: "bg-blue-50"
    }
  ];

  return (
    <div className="min-h-screen bg-[#f8fafc] py-16 px-4 font-sans">
      <div className="max-w-5xl mx-auto">
        
        {/* Header Section */}
        <div className="mb-12">
          <div className="flex items-center gap-2 text-slate-500 mb-4">
            <FileText size={18} />
            <span className="text-sm font-bold uppercase tracking-wider">NDT Technical Portal</span>
          </div>
          <h1 className="text-4xl font-black text-slate-800 mb-4 tracking-tight">ISO Acceptance Criteria</h1>
          <p className="text-lg text-slate-500 max-w-2xl">
            Select the required inspection level below to launch the automated UT evaluation tool.
          </p>
        </div>

        {/* Primary Selection Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-20">
          {levels.map((level) => (
            <button
              key={level.id}
              onClick={() => navigate(level.path)}
              className={`group flex flex-col text-left p-8 bg-white border-2 rounded-3xl transition-all duration-300 shadow-sm hover:shadow-xl hover:-translate-y-1 ${level.color}`}
            >
              <div className={`p-4 rounded-2xl w-fit mb-6 ${level.bgColor}`}>
                {level.icon}
              </div>
              <h3 className="text-xl font-bold text-slate-800 mb-3">{level.title}</h3>
              <p className="text-slate-500 text-sm leading-relaxed mb-8 flex-grow">{level.description}</p>
              <div className="flex items-center gap-2 font-bold text-xs uppercase tracking-widest text-slate-400 group-hover:text-slate-800 transition-colors">
                Launch Tool <ArrowRight size={14} />
              </div>
            </button>
          ))}
        </div>

        {/* --- NEW ADDITION: KNOWLEDGE HUB SECTION --- */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 border-t border-slate-200 pt-16">
          
          {/* Quick Glossary */}
          <div>
            <div className="flex items-center gap-2 mb-6 text-indigo-600">
              <BookOpen size={20} />
              <h2 className="text-lg font-bold uppercase tracking-tight">Key Terminology</h2>
            </div>
            <div className="space-y-6">
              <div className="flex gap-4">
                <div className="shrink-0 w-10 h-10 bg-white shadow-sm border border-slate-100 flex items-center justify-center rounded-lg text-slate-400 font-bold text-xs">h2</div>
                <div>
                  <h4 className="font-bold text-slate-800 text-sm">Embedded Indication</h4>
                  <p className="text-sm text-slate-500">Discontinuities entirely contained within the weld volume, not reaching the surface.</p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="shrink-0 w-10 h-10 bg-white shadow-sm border border-slate-100 flex items-center justify-center rounded-lg text-slate-400 font-bold text-xs">h3</div>
                <div>
                  <h4 className="font-bold text-slate-800 text-sm">Surface Breaking</h4>
                  <p className="text-sm text-slate-500">Indications that extend to either the internal or external surface of the test object.</p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="shrink-0 w-10 h-10 bg-white shadow-sm border border-slate-100 flex items-center justify-center rounded-lg text-slate-400 font-bold text-xs">l_max</div>
                <div>
                  <h4 className="font-bold text-slate-800 text-sm">Maximum Length</h4>
                  <p className="text-sm text-slate-500">The longitudinal limit of an indication before more stringent height (h1) rules apply.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Guidelines / Selection Help */}
          <div className="bg-slate-800 rounded-3xl p-8 text-white relative overflow-hidden">
            <HelpCircle className="absolute -right-4 -top-4 text-white/5" size={160} />
            <h2 className="text-xl font-bold mb-6 flex items-center gap-2">
              <ZoomIn size={20} /> Selection Guide
            </h2>
            <ul className="space-y-4 relative z-10">
              <li className="flex gap-3">
                <div className="mt-1 bg-indigo-500 rounded-full p-1"><Layers size={12}/></div>
                <p className="text-sm text-slate-300"><strong className="text-white">Standard Alignment:</strong> Levels 1, 2, and 3 typically correspond to Quality Levels B, C, and D of ISO 5817.</p>
              </li>
              <li className="flex gap-3">
                <div className="mt-1 bg-indigo-500 rounded-full p-1"><Layers size={12}/></div>
                <p className="text-sm text-slate-300"><strong className="text-white">Thickness Range:</strong> These tools are specifically optimized for ferritic steel thicknesses between 6mm and 100mm+.</p>
              </li>
              <li className="flex gap-3">
                <div className="mt-1 bg-indigo-500 rounded-full p-1"><Layers size={12}/></div>
                <p className="text-sm text-slate-300"><strong className="text-white">Technique:</strong> Values are based on pulse-echo ultrasonic techniques (UT) according to ISO 17640.</p>
              </li>
            </ul>
          </div>
        </div>

        {/* Footer */}
        <footer className="mt-20 pt-8 border-t border-slate-200">
          <p className="text-[10px] text-slate-400 text-center uppercase tracking-[0.3em] font-bold">
            Engineering Compliance Dashboard &bull; ISO 11666 / 23279 Standardized Logic
          </p>
        </footer>
      </div>
    </div>
  );
};

export default ISOResources;